import { Hats } from '../../../@resources/hats';
import { assertVal } from '../../../CodesThatsGottaGoSomewhereElse';
import { toRadians } from '../../../platform/geom/Math';
import { Point } from '../../../platform/geom/Point';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Camera } from '../../canvas/Camera';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { CommonShortcutToolBehavior } from '../../canvas/tooling/CommonShortcutToolBehavior';
import { PanZoomToolBehavior } from '../../canvas/tooling/PanZoomToolBehavior';
import { SlapToolBehavior } from '../../canvas/tooling/SlapToolBehavior';
import { ToolBehavior } from '../../canvas/tooling/ToolBehavior';
import { defineTool } from '../../canvas/tooling/ToolDefinition';
import { ToolOverlay } from '../../canvas/tooling/ToolOverlay';
import { resolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { Colors } from './../../../ui';
import { HatController } from './HatController';
import { HatSettings } from './HatTypes';

type DragRegion = { from: Point, to: Point };

export class ThrowHatToolBehavior extends ToolBehavior {

    @resolve(Camera) private camera!: Camera;
    @resolve(HatController) private ctrl!: HatController;
    @resolve(ProfileModel) private me!: ProfileModel;
    @resolve(ParticipantManager) private participants!: ParticipantManager;
    @resolve(ToolOverlay) private overlay!: ToolOverlay;

    private region = null as DragRegion | null;

    public init() {
        this.burden(this.overlay.add(this.doPaint));
    }

    public getPreferredCursor(state: string) {
        if (state === 'idle' || state === 'confetting') {
            return 'crosshair';
        }
    }

    public onMouseDown(state: string, mi: MouseInput): string | void {
        if (state === 'idle' && mi.button === MouseButtons.Main) {
            const mpt = this.camera.toModelPoint(mi.position);
            this.region = { from: mpt, to: mpt };
            return 'confetting';
        }
    }

    public onMouseMove(state: string, mi: MouseInput): string | void {
        if (state === 'confetting') {
            assertVal(this.region, 'ConfettiToolBehavior: creating without region?');
            this.region.to = this.camera.toModelPoint(mi.position);
            this.overlay.repaint();
        }
    }

    public onMouseUp(state: string, mi: MouseInput): string | void {
        if (state === 'confetting') {
            assertVal(this.region, 'ConfettiToolBehavior: creating without region?');

            this.region.to = this.camera.toModelPoint(mi.position);
            const { from, to } = this.region;

            const dist = Math.abs(from.subtract(to).length());
            if (dist >= 5) {
                this.ctrl.throwUserHat(this.me.id, [from, to]);
            }

            this.region = null;
            this.overlay.repaint();
            return 'idle';
        }
    }

    private doPaint = (gfx: UniversalSurfaceRenderer) => {
        const { modelViewMatrix: mv, scale: cs } = this.camera;
        const { from, to } = this.region ?? {};
        if (!from || !to) return;

        const a = mv.apply(from);
        const b = mv.apply(to);
        const v = Point.vector(b, a).normalize();
        const f = Point.vector(b, a).length() * .2;

        gfx.strokeStyle = Colors.primaryDark;
        gfx.lineWidth = Math.max(f * .05, 1.5);
        gfx.lineCap = 'round';

        gfx.strokeLine(Point.flatten([b, a]));
        gfx.strokeLine(Point.flatten([a, a.add(v.rotate(toRadians(135)).multiply(f))]));
        gfx.strokeLine(Point.flatten([a, a.add(v.rotate(toRadians(-135)).multiply(f))]));

        const type = this.participants.getSelf().hat;
        if (type) {
            const { scale: hs } = HatSettings.lookup(type);

            const img = new Image();
            img.src = Hats[type];

            const whr = (img.height || 0) / (img.width || 1);
            const wh = new Point(40 * hs * cs, 40 * hs * cs * whr);
            const xy = b.subtract(wh.divide(2));

            gfx.drawImage(img, xy.x, xy.y, wh.w, wh.h);
        }
    };
}

export const throwHatTool = defineTool({
    id: 'throwHatTool',
    label: 'Throw Hat',
    category: 'gestures',
    icon: 'deqo://icons/i8/hat',
    weight: 4040,
    behaviors: [
        CommonShortcutToolBehavior,
        ThrowHatToolBehavior,
        PanZoomToolBehavior(),
        SlapToolBehavior,
    ]
});
